import React, { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColumnApi,
  GridApi,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RefreshServerSideParams,
} from 'ag-grid-enterprise';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import * as Bixby from '$/core';
import { CovenantReviewQuickFilterTypeService, CovenantReviewService } from '$/services';
import SortFilter from '$/components/sortFilter';
import { IFilterItem } from '$/components/sortFilter/filterItem';
import Loading from '$/components/loading/Loading';
import SimpleTextFilter from '$/components/global/floatingFilters/SimpleTextFilter';
import CompanyFloatingFilter from '$/components/covenant-review-grid/CompanyFloatingFilter';
import './index.scss';

interface ICovenantReviewGridProps {
  companyId?: string;
  paginationPageSize?: number;
  dispatchToastError: (message: string) => void;
}

export const CovenantReviewGrid: React.FC<ICovenantReviewGridProps> = props => {
  const { companyId, paginationPageSize: paginationPageSize = 100, dispatchToastError } = props;

  const [gridApi, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);
  const [isLoading, setIsLoading] = useState(null as boolean | null);
  const [filterTypes, setFilterTypes] = useState([] as Array<IFilterItem>);
  const [activeFilterType, setActiveFilterType] = useState(undefined as string | undefined);
  const activeFilterTypeRef = useRef(undefined as string | undefined);
  activeFilterTypeRef.current = activeFilterType;

  useEffect(() => {
    async function getFilterTypes() {
      setIsLoading(true);
      try {
        const quickFilterTypes: Bixby.Models.IIdentifiable[] = await CovenantReviewQuickFilterTypeService.getAll();

        const filterItems: IFilterItem[] = quickFilterTypes.map(filter => {
          const { id, name } = filter;
          return { id, name, active: false } as IFilterItem;
        });

        setFilterTypes(filterItems);
      } finally {
        setIsLoading(false);
      }
    }

    getFilterTypes();
  }, []);

  useEffect(() => {
    async function refreshGrid() {
      if (gridApi) {
        gridApi.refreshServerSide({
          purge: true,
        } as RefreshServerSideParams);
      }
    }

    refreshGrid();
  }, [gridApi, activeFilterType]);

  const getServerSideDatasource: () => IServerSideDatasource = () => {
    return {
      getRows: async (params: IServerSideGetRowsParams) => {
        const { startRow, filterModel } = params.request;

        let creditSightsCompanyId = null;

        if (filterModel) {
          creditSightsCompanyId = filterModel['companyName']?.id;
        }

        let results: Bixby.Models.PagedResult<Bixby.Models.CovenantReviewArticle>;

        try {
          if (companyId) {
            results = await CovenantReviewService.getCovenantReviewGlobalFeedForCompanyAsync({
              skip: startRow || 0,
              take: paginationPageSize,
              covenantReviewQuickFilterTypeId: activeFilterTypeRef.current,
              bixbyCompanyId: companyId,
              creditSightsCompanyId: null,
            });
          } else if (creditSightsCompanyId) {
            results = await CovenantReviewService.getCovenantReviewGlobalFeedForCompanyAsync({
              skip: startRow || 0,
              take: paginationPageSize,
              covenantReviewQuickFilterTypeId: activeFilterTypeRef.current,
              bixbyCompanyId: null,
              creditSightsCompanyId: creditSightsCompanyId,
            });
          } else {
            results = await CovenantReviewService.getCovenantReviewGlobalFeedAsync({
              skip: startRow || 0,
              take: paginationPageSize,
              covenantReviewQuickFilterTypeId: activeFilterTypeRef.current,
            });
          }

          params.success({
            rowData: results.results,
            rowCount: results.totalResults,
          });
        } catch (e: any) {
          dispatchToastError('Error fetching Covenant Review articles');
          params.fail();
        }
      },
    };
  };

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
    const datasource = getServerSideDatasource();
    params.api.setGridOption('serverSideDatasource', datasource);
  };

  const articleTitleCellRenderer = (props: any) => (
    <Link className="link_underline" to={`${props.data.articleUrl}`}>
      {props.data.articleTitle}
    </Link>
  );

  const companyNameCellRenderer = (props: any) => {
    return props.data.companyUrl ? (
      <Link className="link_underline" to={`${props.data.companyUrl}`}>
        {props.data.companyName}
      </Link>
    ) : (
      <div>-</div>
    );
  };

  const handleFilter = (id: string, isFilterActive: boolean) => {
    if (isFilterActive) {
      setActiveFilterType(id);
    } else {
      setActiveFilterType(undefined);
    }
  };

  const handleClearFilters = () => {
    setActiveFilterType(undefined);
  };

  if (isLoading) {
    return <Loading pageLevel />;
  } else {
    return (
      <React.Fragment>
        <div className={classNames('covenant-review-container', { company: companyId })}>
          <SortFilter
            filters={filterTypes}
            multiselect={false}
            onFilter={handleFilter}
            onClearFilters={handleClearFilters}
          />
          <div className="covenant-review-data-grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <AgGridReact
                  defaultColDef={{
                    flex: 1,
                    minWidth: 100,
                    sortable: false,
                    resizable: true,
                    cellClass: 'align-center',
                    suppressMenu: true,
                  }}
                  enableRangeSelection={false}
                  reactiveCustomComponents
                  suppressContextMenu={true}
                  suppressDragLeaveHidesColumns={true}
                  suppressMovableColumns={true}
                  animateRows={true}
                  rowHeight={32}
                  headerHeight={32}
                  rowModelType="serverSide"
                  pagination={true}
                  paginationPageSize={paginationPageSize}
                  cacheBlockSize={paginationPageSize}
                  onGridReady={onGridReady}
                  components={{
                    articleTitleCellRenderer,
                    companyNameCellRenderer,
                  }}
                  columnDefs={[
                    {
                      field: 'companyName',
                      headerName: 'Company',
                      cellClass: 'align-left',
                      cellRenderer: 'companyNameCellRenderer',
                      filter: companyId ? false : SimpleTextFilter,
                      floatingFilter: companyId ? false : true,
                      floatingFilterComponent: companyId ? null : CompanyFloatingFilter,
                      filterParams: companyId
                        ? null
                        : {
                            dispatchToastError,
                          },
                      floatingFilterComponentParams: companyId
                        ? null
                        : {
                            suppressFilterButton: true,
                          },
                    },
                    {
                      field: 'articleTitle',
                      headerName: 'Article',
                      cellClass: 'align-left',
                      cellRenderer: 'articleTitleCellRenderer',
                      flex: 4,
                    },
                    {
                      field: 'publishDate',
                      headerName: 'Publish Date',
                      cellRenderer: (params: ICellRendererParams) => {
                        const date = new Date(params.value);
                        return params.value
                          ? Bixby.Utils.dateIsToday(date)
                            ? Bixby.Utils.dateToStringFormat(date, 'hh:mm A')
                            : Bixby.Utils.dateToStringFormat(date, 'DD MMM YY')
                          : '';
                      },
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
};
